import { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Radio,
  ColorPicker, App as AntdApp, Spin, Row, Col, Grid,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import dayjs from 'dayjs';
import {
  projectService,
  type ProjectDetail, type WorkflowMode, type ProjectStatus,
} from '@/services/projectService';
import { orgService } from '@/services/rolesService';

/**
 * V1.0.36: Modal sửa thông tin dự án.
 * Cho phép owner / manager / SYS_ADMIN sửa các trường:
 *  - name, description, color, departmentIds (multi), workflowMode, dateRange, status
 * KHÔNG sửa được: code, owner, members (qua modal khác).
 */
interface FormValues {
  name: string;
  description?: string;
  color?: string;
  departmentIds?: string[];
  workflowMode: WorkflowMode;
  status: ProjectStatus;
  // V1.0.56: Tách dateRange → 2 field riêng (mobile-friendly)
  startDate?: dayjs.Dayjs | null;
  endDate?: dayjs.Dayjs | null;
}

export function EditProjectModal({
  open, onClose, onUpdated, projectId,
}: {
  open: boolean;
  onClose: () => void;
  onUpdated: (project: ProjectDetail) => void;
  projectId: string;
}) {
  const [form] = Form.useForm<FormValues>();
  const { message } = AntdApp.useApp();
  const [submitting, setSubmitting] = useState(false);

  // V1.0.56: Detect mobile cho UX chọn ngày tốt hơn
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  // Load project detail để pre-fill form
  const { data: detail, isLoading: loadingDetail } = useQuery({
    queryKey: ['project-detail', projectId, 'for-edit'],
    queryFn: () => projectService.getById(projectId),
    enabled: open && !!projectId,
  });

  // Pre-fill form khi detail load xong
  useEffect(() => {
    if (!open || !detail) return;
    const sharedDeptIds = (detail.sharedWithDepartments ?? []).map(d => d.id);
    // Đảm bảo primary ở phần tử đầu
    const primaryFirst = detail.departmentId
      ? [detail.departmentId, ...sharedDeptIds.filter(id => id !== detail.departmentId)]
      : sharedDeptIds;

    form.setFieldsValue({
      name: detail.name,
      description: detail.description ?? undefined,
      color: detail.color ?? '#1976D2',
      departmentIds: primaryFirst,
      workflowMode: detail.workflowMode as WorkflowMode,
      status: detail.status as ProjectStatus,
      // V1.0.56: Init 2 field riêng
      startDate: detail.startDate ? dayjs(detail.startDate) : null,
      endDate: detail.endDate ? dayjs(detail.endDate) : null,
    });
  }, [open, detail, form]);

  // Departments tree
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => orgService.getTree(),
    enabled: open,
  });
  const flattenOrg = (nodes: any[], prefix = '', currentDeptIds: string[] = []): Array<{label: string; value: string}> => {
    const out: Array<{label: string; value: string}> = [];
    for (const n of nodes) {
      // V1.0.47: Bỏ qua khoa inactive, NHƯNG vẫn giữ nếu là khoa đang được gán vào project (tránh mất data khi edit)
      if (n.isActive === false && !currentDeptIds.includes(n.id)) {
        if (n.children?.length) out.push(...flattenOrg(n.children, prefix, currentDeptIds));
        continue;
      }
      const label = n.isActive === false ? `${prefix}${n.name} (đã ẩn)` : `${prefix}${n.name}`;
      out.push({ label, value: n.id });
      if (n.children?.length) out.push(...flattenOrg(n.children, prefix + '— ', currentDeptIds));
    }
    return out;
  };
  // V1.0.47: Pass currentDeptIds để giữ khoa đang gán dù đã inactive
  const currentDeptIds: string[] = detail ? [
    (detail as any).departmentId,
    ...((detail as any).sharedWithDepartments?.map((d: any) => d.id) ?? []),
  ].filter(Boolean) : [];
  const deptOptions = flattenOrg(orgTree, '', currentDeptIds);

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      setSubmitting(true);

      // Phần tử đầu = primary, các phần tử sau = additional
      const deptIds: string[] = Array.isArray(v.departmentIds) ? v.departmentIds : [];
      const primaryDept = deptIds[0];
      const additionalDepts = deptIds.slice(1);

      const updated = await projectService.update(projectId, {
        name: v.name.trim(),
        description: v.description?.trim() || undefined,
        color: typeof v.color === 'string' ? v.color : (v.color as any)?.toHexString?.() ?? '#1976D2',
        departmentId: primaryDept || undefined,
        additionalDepartmentIds: additionalDepts, // empty array = clear all phụ, còn lại primary
        workflowMode: v.workflowMode,
        status: v.status,
        startDate: v.startDate?.format('YYYY-MM-DD'),
        endDate: v.endDate?.format('YYYY-MM-DD'),
      });

      message.success('Đã cập nhật dự án');
      onUpdated(updated);
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e.response?.data?.message ?? 'Cập nhật dự án thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleSubmit}
      okText="Lưu thay đổi"
      cancelText="Hủy"
      confirmLoading={submitting}
      title={detail ? `Sửa dự án: ${detail.name}` : 'Sửa dự án'}
      width={isMobile ? '95vw' : 620}
      style={isMobile ? { top: 8 } : undefined}
      destroyOnClose
    >
      {loadingDetail ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="name" label="Tên dự án"
            rules={[{ required: true, message: 'Bắt buộc nhập tên' }]}
          >
            <Input placeholder="VD: Tuyển dụng KS Q2/2026" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item label="Mã dự án">
              <Input value={detail?.code ?? ''} disabled />
            </Form.Item>
            <Form.Item name="color" label="Màu chủ đạo">
              <ColorPicker showText format="hex" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} placeholder="Mục tiêu, phạm vi của dự án..." />
          </Form.Item>

          <Form.Item
            name="departmentIds"
            label="Khoa được xem dự án"
            extra="Khoa đầu tiên = khoa chủ quản. Có thể chọn thêm khoa phụ — toàn bộ nhân viên các khoa này đều thấy dự án."
          >
            <Select
              mode="multiple"
              placeholder="Chọn 1 hoặc nhiều khoa/phòng"
              options={deptOptions}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
            />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="status" label="Trạng thái" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'DRAFT', label: 'Bản nháp' },
                  { value: 'ACTIVE', label: 'Đang chạy' },
                  { value: 'ON_HOLD', label: 'Tạm dừng' },
                  { value: 'COMPLETED', label: 'Hoàn thành' },
                  { value: 'ARCHIVED', label: 'Lưu trữ' },
                ]}
              />
            </Form.Item>
            <Form.Item name="workflowMode" label="Quy trình" rules={[{ required: true }]}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                options={[
                  { label: <span><GitBranch size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Song song</span>, value: 'PARALLEL' },
                  { label: <span><GitBranch size={11} style={{ verticalAlign: -2, marginRight: 4, transform: 'rotate(90deg)' }} />Tuần tự</span>, value: 'SEQUENTIAL' },
                ]}
              />
            </Form.Item>
          </div>

          {/* V1.0.56: Tách RangePicker → 2 DatePicker riêng cho mobile-friendly */}
          <Form.Item label="Thời gian dự án" style={{ marginBottom: 8 }}>
            <Row gutter={isMobile ? [0, 8] : 12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startDate"
                  noStyle
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const end = getFieldValue('endDate');
                        if (value && end && value.isAfter(end)) {
                          return Promise.reject(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="Ngày bắt đầu"
                    style={{ width: '100%' }}
                    inputReadOnly={isMobile}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="endDate"
                  noStyle
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('startDate');
                        if (value && start && value.isBefore(start)) {
                          return Promise.reject(new Error('Ngày kết thúc phải sau ngày bắt đầu'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="Ngày kết thúc"
                    style={{ width: '100%' }}
                    inputReadOnly={isMobile}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <div style={{ fontSize: 11, color: '#7d8a98', padding: 8, background: '#f0f4f8', borderRadius: 6, marginTop: 4 }}>
            💡 Để thêm/xóa thành viên dự án, vào trang dự án → Menu thao tác → "Quản lý thành viên"
          </div>
        </Form>
      )}
    </Modal>
  );
}
